import { Loader2, UserPlus } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

import {
  adminClient,
  type AdminStudioPendingGrant,
  type AdminStudioSeat,
  type AdminStudioSeatRole,
  type AdminStudioSeatsResponse,
} from '@/api/adminClient';
import { OrgLegalAcceptances } from '@/components/legal/OrgLegalAcceptances';
import { StatusPill } from '@/components/runs/StatusPill';
import { AdminPageHeader, PAGE_CONTAINER_CLASS } from '@/components/shell/AdminPageHeader';
import { DataTable, DataTableCell, DataTableHead, DataTableLoadError, DataTableMessage, DataTableRow } from '@/components/table/DataTable';
import { SearchInput } from '@/components/table/SearchInput';
import { SegmentedFilter } from '@/components/table/SegmentedFilter';
import { TablePager } from '@/components/table/TablePager';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { usePagedOffset } from '@/hooks/usePagedOffset';
import { resolveLoadStatus } from '@/lib/loadState';
import { orgLabel } from '@/lib/format';
import { AddPeopleDialog } from '@/pages/org/AddPeopleDialog';
import { useOrgScope } from '@/pages/org/OrgLayout';

const MEMBER_PAGE_SIZE = 25;

type RoleFilter = 'all' | AdminStudioSeatRole;

const ROLE_LABELS: Record<AdminStudioSeatRole, string> = { owner: 'Owner', admin: 'Admin', member: 'Member' };

const COLUMNS = [{ label: 'Member' }, { label: 'Role' }, { label: 'Status' }, { label: '', className: 'w-24' }];

type PendingAction =
  | { kind: 'revoke'; seat: AdminStudioSeat }
  | { kind: 'withdraw'; grant: AdminStudioPendingGrant };

function MemberAvatar({ seat }: { seat: AdminStudioSeat }) {
  if (seat.picture) {
    return <img src={seat.picture} alt="" className="h-8 w-8 shrink-0 rounded-full bg-muted" />;
  }
  return (
    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-medium text-muted-foreground">
      {(seat.displayName || seat.email || '?').charAt(0).toUpperCase()}
    </div>
  );
}

/**
 * Studio seats for one organization. Organization membership alone is not
 * Studio access: a new org gets its first owner here, and that owner invites
 * the rest of their team from Studio. A grant by email waits until the person first
 * signs in.
 */
export function OrgMembersPage() {
  const { orgId, org, reloadOrg } = useOrgScope();
  const [data, setData] = useState<AdminStudioSeatsResponse | undefined>();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | undefined>();
  const [query, setQuery] = useState('');
  const [role, setRole] = useState<RoleFilter>('all');
  const search = useDebouncedValue(query.trim());
  const [offset, setOffset] = usePagedOffset(`${search}|${role}`);
  const [grantOpen, setGrantOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingAction | undefined>();
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(undefined);
    try {
      setData(
        await adminClient.listStudioSeats(orgId, {
          limit: MEMBER_PAGE_SIZE,
          offset,
          q: search || undefined,
          role: role === 'all' ? undefined : role,
        }),
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load people';
      setLoadError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [orgId, offset, search, role]);

  useEffect(() => {
    void load();
  }, [load]);

  const afterChange = async () => {
    await Promise.all([load(), reloadOrg().catch(() => undefined)]);
  };

  const confirmAction = async () => {
    if (!pendingAction) return;
    setBusy(true);
    try {
      if (pendingAction.kind === 'revoke') {
        await adminClient.revokeStudioSeat(orgId, pendingAction.seat.userId);
        toast.success('Access revoked');
      } else {
        await adminClient.withdrawStudioPendingGrant(orgId, pendingAction.grant.email);
        toast.success('Invitation withdrawn');
      }
      setPendingAction(undefined);
      await afterChange();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update access');
    } finally {
      setBusy(false);
    }
  };

  const seats = data?.seats ?? [];
  const pendingGrants = data?.pendingGrants ?? [];
  const counts = data?.roleCounts;
  const status = resolveLoadStatus({ loaded: !loading, error: loadError, hasData: Boolean(data) });

  return (
    <div className={PAGE_CONTAINER_CLASS}>
      <AdminPageHeader
        title="People"
        description="People with a Studio seat. Belonging to the WorkOS organization alone does not grant Studio."
        actions={
          <Button type="button" onClick={() => setGrantOpen(true)}>
            <UserPlus className="mr-2 h-4 w-4" aria-hidden="true" />
            Add people
          </Button>
        }
      />

      {pendingGrants.length > 0 ? (
        <section className="mb-6 overflow-hidden rounded-xl border border-border bg-card">
          <div className="border-b border-border px-4 py-3">
            <h2 className="text-sm font-semibold text-foreground">Invited</h2>
            <p className="text-xs text-muted-foreground">Their seat starts the first time they sign in.</p>
          </div>
          <ul className="divide-y divide-border">
            {pendingGrants.map((grant) => (
              <li key={grant.email} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                <span className="min-w-0 flex-1 truncate">{grant.email}</span>
                <StatusPill tone="neutral">{ROLE_LABELS[grant.role]}</StatusPill>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setPendingAction({ kind: 'withdraw', grant })}
                >
                  Withdraw
                </Button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <DataTable
        toolbar={
          <>
            <SearchInput value={query} onChange={setQuery} placeholder="Search name or email" label="Search people" />
            <SegmentedFilter<RoleFilter>
              label="Role"
              value={role}
              onChange={setRole}
              options={[
                { id: 'all', label: 'All', count: data?.seatCount },
                { id: 'owner', label: 'Owners', count: counts?.owner },
                { id: 'admin', label: 'Admins', count: counts?.admin },
                { id: 'member', label: 'Members', count: counts?.member },
              ]}
            />
          </>
        }
        footer={
          <TablePager
            total={data?.total ?? 0}
            limit={MEMBER_PAGE_SIZE}
            offset={offset}
            disabled={loading}
            onOffsetChange={setOffset}
          />
        }
      >
        <DataTableHead columns={COLUMNS} />
        {status === 'loading' ? (
          <DataTableMessage colSpan={COLUMNS.length} loading>
            Loading people
          </DataTableMessage>
        ) : status === 'error' ? (
          <DataTableLoadError colSpan={COLUMNS.length} message={loadError} onRetry={() => void load()} />
        ) : seats.length === 0 ? (
          <DataTableMessage colSpan={COLUMNS.length}>
            {search || role !== 'all'
              ? 'No one matches these filters'
              : 'No one in this organization can open Studio yet. Add the first owner to get them started.'}
          </DataTableMessage>
        ) : (
          <tbody className={loading ? 'opacity-60' : undefined}>
            {seats.map((seat) => (
              <DataTableRow key={seat.userId}>
                <DataTableCell>
                  <div className="flex min-w-0 items-center gap-3">
                    <MemberAvatar seat={seat} />
                    <div className="min-w-0">
                      <p className="truncate font-medium text-foreground">
                        {seat.displayName || seat.email || 'Unknown user'}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">
                        {seat.displayName && seat.email ? seat.email : seat.userId}
                      </p>
                    </div>
                  </div>
                </DataTableCell>
                <DataTableCell>{ROLE_LABELS[seat.role]}</DataTableCell>
                <DataTableCell>
                  <StatusPill tone={seat.active ? 'success' : 'warning'}>
                    {seat.active ? 'Active' : 'Suspended'}
                  </StatusPill>
                </DataTableCell>
                <DataTableCell className="text-right">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setPendingAction({ kind: 'revoke', seat })}
                  >
                    Revoke
                  </Button>
                </DataTableCell>
              </DataTableRow>
            ))}
          </tbody>
        )}
      </DataTable>

      <OrgLegalAcceptances orgId={orgId} />

      <AddPeopleDialog
        open={grantOpen}
        orgId={orgId}
        orgName={orgLabel(org)}
        defaultRole={data && data.seatCount > 0 ? 'member' : 'owner'}
        onClose={() => setGrantOpen(false)}
        onAdded={() => {
          setGrantOpen(false);
          void afterChange();
        }}
      />

      <Dialog
        open={Boolean(pendingAction)}
        onClose={() => setPendingAction(undefined)}
        title={pendingAction?.kind === 'withdraw' ? 'Withdraw invitation?' : 'Revoke Studio access?'}
        description={
          pendingAction?.kind === 'withdraw'
            ? `${pendingAction.grant.email} will not get a seat when they sign in.`
            : pendingAction
              ? `${pendingAction.seat.displayName || pendingAction.seat.email || pendingAction.seat.userId} loses access to Studio for this organization. Their projects stay.`
              : undefined
        }
        footer={
          <>
            <Button type="button" variant="outline" onClick={() => setPendingAction(undefined)} disabled={busy}>
              Cancel
            </Button>
            <Button type="button" variant="destructive" onClick={() => void confirmAction()} disabled={busy}>
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : null}
              {pendingAction?.kind === 'withdraw' ? 'Withdraw' : 'Revoke access'}
            </Button>
          </>
        }
      >
        <p className="text-sm text-muted-foreground">You can grant access again at any time.</p>
      </Dialog>
    </div>
  );
}
